import { TitulationLoader } from './parts/TitulationLoader';
import { Alert } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '../../../../../../components/text';
import Table from './parts/Table';
import { DegreeDocuments } from '../../../../../StudentsDegreeStudent/parts/TitulationDocuments';
import { PaymentStatus } from '../../../../../StudentsDegreeStudent/parts/PaymentStatus';
import FormView from '../../../../../StudentsDegreeStudent/parts/FormView';

type Props = {
  studyPlanEnrollmentId: string;
};

export function Titulation({ studyPlanEnrollmentId }: Props) {
  const dictPrefix = 'studentsDegreeStudent';
  const { t } = useTranslation();
  const texts = useMemo(
    () => ({
      title: t(`${dictPrefix}.title`),
      subtitle: t(`${dictPrefix}.subtitle`),
      subtitleWitoutActivities: t(`${dictPrefix}.subtitleWitoutActivities`),
      box: t<
        string,
        Record<
          'student' | 'studyPlan' | 'schedule' | 'semester' | 'campus',
          string
        >
      >(`${dictPrefix}.box`, {
        returnObjects: true,
      }),
      noActivities: t(`${dictPrefix}.noActivities`),
      degreeDocuments: {
        title: t(`${dictPrefix}.degreeDocuments.title`),
        selectOptions: {
          rejected: t(`${dictPrefix}.degreeDocuments.selectOptions.rejected`),
          approved: t(`${dictPrefix}.degreeDocuments.selectOptions.approved`),
          pending: t(`${dictPrefix}.degreeDocuments.selectOptions.pending`),
        },
        toasts: {
          upload: {
            success: t(`${dictPrefix}.degreeDocuments.toasts.upload.success`),
            failed: t(`${dictPrefix}.degreeDocuments.toasts.upload.failed`),
          },
          remove: {
            success: t(`${dictPrefix}.degreeDocuments.toasts.remove.success`),
            failed: t(`${dictPrefix}.degreeDocuments.toasts.remove.failed`),
          },
          editStatus: {
            success: t(
              `${dictPrefix}.degreeDocuments.toasts.editStatus.success`,
            ),
            failed: t(`${dictPrefix}.degreeDocuments.toasts.editStatus.failed`),
          },
          confirmDelete: {
            title: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.title`,
            ),
            description: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.description`,
            ),
            confirm: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.confirm`,
            ),
            cancel: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.cancel`,
            ),
          },
        },
        table: {
          noResults: t(`${dictPrefix}.degreeDocuments.table.noResults`),
          headers: {
            name: t(`${dictPrefix}.degreeDocuments.table.headers.name`),
            documentName: t(
              `${dictPrefix}.degreeDocuments.table.headers.documentName`,
            ),
            load: t(`${dictPrefix}.degreeDocuments.table.headers.load`),
            state: t(`${dictPrefix}.degreeDocuments.table.headers.state`),
          },
          values: {
            loadButton: t(
              `${dictPrefix}.degreeDocuments.table.values.loadButton`,
            ),
          },
        },
      },
      paymentStatus: {
        title: t(`${dictPrefix}.paymentStatus.title`),
        table: {
          noResults: t(`${dictPrefix}.paymentStatus.table.noResults`),
          headers: {
            concept: t(`${dictPrefix}.paymentStatus.table.headers.concept`),
            amount: t(`${dictPrefix}.paymentStatus.table.headers.amount`),
            paymentDate: t(
              `${dictPrefix}.paymentStatus.table.headers.paymentDate`,
            ),
            status: t(`${dictPrefix}.paymentStatus.table.headers.status`),
            action: t(`${dictPrefix}.paymentStatus.table.headers.action`),
          },
          values: {
            concept: t(`${dictPrefix}.paymentStatus.table.values.concept`),
            button: t(`${dictPrefix}.paymentStatus.table.values.button`),
            payment: {
              pending: t(
                `${dictPrefix}.paymentStatus.table.values.payment.pending`,
              ),
              paid: t(`${dictPrefix}.paymentStatus.table.values.payment.paid`),
            },
          },
        },
      },
    }),
    [t],
  );
  return (
    <TitulationLoader studyPlanEnrollmentId={studyPlanEnrollmentId}>
      {({ data }) => (
        <section className="petition-summary mb-4">
          <div className="mb-3">
            <SectionTitle text={texts.title} />
          </div>
          <div className="mb-3">
            {Boolean(data.degreeProcess.activities.length) ? (
              <p>{texts.subtitle}</p>
            ) : (
              <p>{texts.subtitleWitoutActivities}</p>
            )}
          </div>
          {Boolean(data.degreeProcess.activities.length) && (
            <div className="mb-4">
              <Table
                data={data.degreeProcess.activities}
                gradeAverage={data.degreeProcess.gradeAverage}
              />
            </div>
          )}
          {!data.degreeProcess.activities.length && (
            <div className="mb-4">
              <Alert
                className=""
                color="info"
                icon="information"
                size="lg"
                text={texts.noActivities}
              />
            </div>
          )}
          <DegreeDocuments
            studyPlanEnrollmentId={data.studyPlanEnrollment.id}
            documents={data.degreeDocuments}
            texts={texts.degreeDocuments}
            refresh={() => {}}
            disableEditing
          />
          {!!data.paymentInfo && (
            <PaymentStatus
              data={data.paymentInfo}
              texts={texts.paymentStatus}
            />
          )}
          {data.degreeProcess.summary && (
            <FormView
              date={data.degreeProcess.summary.date}
              createdAt={data.degreeProcess.summary.createdAt}
              graduationGrade={data.degreeProcess.summary.gradeGraduation}
              degreeGrade={data.degreeProcess.summary.gradeDegreeProcess}
              registerCode={data.degreeProcess.summary.registerCode}
              certificateNumber={data.degreeProcess.summary.certificateNumber}
              registryBook={data.degreeProcess.summary.registryBook}
              distinctionRank={data.degreeProcess.summary.distinctionRank?.name}
            />
          )}
        </section>
      )}
    </TitulationLoader>
  );
}
